<template>
    <div>
        <div class="titulo">
            <div class="margem container">
                <h2>Gestão de IA</h2>
            </div>
        </div>
        <div class="margem container">
            <div class="tags m-b">
                <a
                    v-for="tab in tabs"
                    :key="tab.id"
                    href="#"
                    :class="{ ativo: activeTab === tab.id, 'em-breve': tab.kind === 'upcoming' }"
                    @click.prevent="selecionarAba(tab.id)"
                >
                    {{ tab.label }}
                    <span v-if="tab.kind === 'upcoming'" class="ia-badge">Em breve</span>
                </a>
            </div>

            <section v-if="activeTab === 'usage'" class="ia-screen">
                <p class="ia-lead">Consumo de tokens e custo estimado de todos os fluxos de IA no período selecionado.</p>
                <div class="bloco margem ia-toolbar">
                    <label>De
                        <input v-model="usage.filters.from" type="date" @change="carregarConsumo">
                    </label>
                    <label>Até
                        <input v-model="usage.filters.until" type="date" @change="carregarConsumo">
                    </label>
                    <label>Módulo
                        <select v-model="usage.filters.module" @change="carregarConsumo">
                            <option value="">Todos</option>
                            <option v-for="module in modules" :key="module" :value="module">{{ moduleName(module) }}</option>
                        </select>
                    </label>
                    <label>Feature
                        <select v-model="usage.filters.feature" @change="carregarConsumo">
                            <option value="">Todas</option>
                            <option v-for="feature in usage.features" :key="feature.value" :value="feature.value">
                                {{ feature.label }}
                            </option>
                        </select>
                    </label>
                </div>
                <div v-if="usage.loading" class="bloco margem ia-state">Carregando consumo...</div>
                <template v-else>
                    <div v-if="usage.latency.turns" class="bloco margem">
                        <div class="ia-panel-head"><h3>Tempo de resposta do assistente</h3></div>
                        <p class="ia-lead ia-latency-lead">
                            Média e o turno mais lento no período. A fila é a espera para começar,
                            a pré-busca monta o contexto e o agente inclui o modelo e as ferramentas.
                        </p>
                        <div class="ia-metrics" :class="{ 'ia-metrics-4': usage.latency.stages.length > 3 }">
                            <div v-for="stage in usage.latency.stages" :key="stage.key">
                                <span>{{ stage.label }}</span>
                                <strong>{{ formatMeasure(stage) }}</strong>
                                <small>mais lento {{ formatMeasureMax(stage) }}</small>
                            </div>
                        </div>
                        <table class="tabela">
                            <thead>
                                <tr>
                                    <th>Quando</th>
                                    <th class="alinha-direita">Fila</th>
                                    <th class="alinha-direita">Pré-busca</th>
                                    <th class="alinha-direita">Agente</th>
                                    <th class="alinha-direita">Ferramentas</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(turn, index) in usage.latency.recent" :key="'lat-'+index">
                                    <td>{{ formatDate(turn.created_at) }}</td>
                                    <td class="alinha-direita">{{ formatDuration(turn.queue_wait_ms) }}</td>
                                    <td class="alinha-direita">{{ formatDuration(turn.preflight_ms) }}</td>
                                    <td class="alinha-direita">{{ formatDuration(turn.agno_ms) }}</td>
                                    <td class="alinha-direita">{{ turn.tool_rounds ?? '—' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="bloco margem ia-metrics">
                        <div>
                            <span>Tokens</span>
                            <strong>{{ formatTokens(usage.summary.tokens) }}</strong>
                        </div>
                        <div>
                            <span>Custo estimado</span>
                            <strong>{{ formatCost(usage.summary.cost, usage.currency) }}</strong>
                        </div>
                        <div>
                            <span>Eventos</span>
                            <strong>{{ usage.summary.events || 0 }}</strong>
                        </div>
                    </div>
                    <p v-if="!usage.summary.events" class="ia-lead">Nenhum consumo no período.</p>
                    <template v-else>
                        <div class="ia-columns">
                            <div class="bloco coluna">
                                <div class="ia-panel-head"><h3>Evolução</h3></div>
                                <table class="tabela">
                                    <thead>
                                        <tr>
                                            <th>Dia</th>
                                            <th class="alinha-direita">Tokens</th>
                                            <th class="alinha-direita">Custo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="row in usage.series" :key="row.day">
                                            <td>{{ formatDay(row.day) }}</td>
                                            <td class="alinha-direita">{{ formatTokens(row.tokens) }}</td>
                                            <td class="alinha-direita">{{ formatCost(row.cost, usage.currency) }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="bloco coluna">
                                <div class="ia-panel-head"><h3>Por usuário</h3></div>
                                <table class="tabela">
                                    <thead>
                                        <tr>
                                            <th>Usuário</th>
                                            <th class="alinha-direita">Tokens</th>
                                            <th class="alinha-direita">Custo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="row in usage.byUser" :key="'u-'+row.key">
                                            <td>{{ userName(row) }}</td>
                                            <td class="alinha-direita">{{ formatTokens(row.tokens) }}</td>
                                            <td class="alinha-direita">{{ formatCost(row.cost, usage.currency) }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div class="ia-columns ia-columns-3">
                            <div class="bloco coluna">
                                <div class="ia-panel-head"><h3>Por módulo</h3></div>
                                <table class="tabela">
                                    <thead>
                                        <tr>
                                            <th>Módulo</th>
                                            <th class="alinha-direita">Tokens</th>
                                            <th class="alinha-direita">Custo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="row in usage.byModule" :key="'m-'+row.key">
                                            <td>{{ moduleName(row.key) }}</td>
                                            <td class="alinha-direita">{{ formatTokens(row.tokens) }}</td>
                                            <td class="alinha-direita">{{ formatCost(row.cost, usage.currency) }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="bloco coluna">
                                <div class="ia-panel-head"><h3>Por feature</h3></div>
                                <table class="tabela">
                                    <thead>
                                        <tr>
                                            <th>Feature</th>
                                            <th class="alinha-direita">Tokens</th>
                                            <th class="alinha-direita">Custo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="row in usage.byFeature" :key="'f-'+row.key">
                                            <td>{{ featureName(row.key) }}</td>
                                            <td class="alinha-direita">{{ formatTokens(row.tokens) }}</td>
                                            <td class="alinha-direita">{{ formatCost(row.cost, usage.currency) }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="bloco coluna">
                                <div class="ia-panel-head"><h3>Por modelo</h3></div>
                                <table class="tabela">
                                    <thead>
                                        <tr>
                                            <th>Modelo</th>
                                            <th class="alinha-direita">Tokens</th>
                                            <th class="alinha-direita">Custo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="row in usage.byModel" :key="'model-'+row.key">
                                            <td>{{ row.key || '—' }}</td>
                                            <td class="alinha-direita">{{ formatTokens(row.tokens) }}</td>
                                            <td class="alinha-direita">{{ formatCost(row.cost, usage.currency) }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </template>
                </template>
            </section>

            <section v-if="activeTab === 'users'" class="ia-screen">
                <p class="ia-lead">
                    A funcionalidade {{ accessFunctionalityId }} é a chave global.
                    Sem ela, as permissões abaixo não liberam a IA. Os módulos refinam o que cada pessoa pode usar.
                </p>
                <div class="bloco margem ia-toolbar">
                    <label class="ia-search">Buscar
                        <input v-model="access.q" type="search" placeholder="Nome ou e-mail" @keyup.enter="carregarAcesso()">
                    </label>
                    <button class="acao-secundaria" type="button" @click="carregarAcesso()">Filtrar</button>
                    <span v-if="!access.loading" class="ia-count">{{ access.users.length }} {{ access.users.length === 1 ? 'pessoa' : 'pessoas' }}</span>
                </div>
                <div v-if="access.loading" class="bloco margem ia-state">Carregando usuários...</div>
                <div v-else class="bloco margem ia-access-card">
                    <p v-if="!access.users.length" class="ia-state">Nenhuma pessoa encontrada.</p>
                    <table v-else class="tabela ia-access">
                        <thead>
                            <tr>
                                <th>Usuário</th>
                                <th class="alinha-centro">Chave global</th>
                                <th class="alinha-centro">IA habilitada</th>
                                <th v-for="module in accessModules" :key="module" class="alinha-centro">{{ moduleName(module) }}</th>
                                <th class="alinha-centro">Pesquisa</th>
                                <th class="alinha-centro">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in access.users" :key="item.id">
                                <td>
                                    <div class="ia-person">
                                        <strong>{{ item.name }}</strong>
                                        <small>{{ item.email }}</small>
                                        <span v-if="item.blocked_by_functionality" class="ia-status disabled">Sem a chave global</span>
                                        <span v-else-if="access.savingIds.includes(item.id)" class="ia-saving">Salvando...</span>
                                    </div>
                                </td>
                                <td class="alinha-centro">{{ item.has_access_functionality ? 'Sim' : 'Não' }}</td>
                                <td class="alinha-centro">
                                    <input type="checkbox" :checked="item.enabled !== false" @change="toggleEnabled(item, $event)">
                                </td>
                                <td v-for="module in accessModules" :key="item.id+module" class="alinha-centro">
                                    <input
                                        type="checkbox"
                                        :checked="item.module_overrides?.[module] !== false"
                                        :disabled="item.blocked_by_functionality"
                                        @change="toggleModule(item, module, $event)"
                                    >
                                </td>
                                <td class="alinha-centro">
                                    <input
                                        type="checkbox"
                                        :checked="item.feature_overrides?.internal_research !== false"
                                        :disabled="item.blocked_by_functionality"
                                        @change="toggleFeature(item, 'internal_research', $event)"
                                    >
                                </td>
                                <td class="alinha-centro">
                                    <input
                                        type="checkbox"
                                        :checked="item.feature_overrides?.actions !== false"
                                        :disabled="item.blocked_by_functionality"
                                        @change="toggleFeature(item, 'actions', $event)"
                                    >
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            <section v-if="activeTab === 'knowledge'">
                <div class="bloco margem ia-copy">
                    <p v-for="(paragraph, index) in explanation" :key="index">{{ paragraph }}</p>
                </div>
                <div class="ia-filters bloco margem">
                    <label>Módulo
                        <select v-model="knowledgeFilters.module" @change="carregar">
                            <option value="">Todos</option>
                            <option v-for="module in modules" :key="module" :value="module">{{ moduleName(module) }}</option>
                        </select>
                    </label>
                    <label>Status
                        <select v-model="knowledgeFilters.status" @change="carregar">
                            <option value="">Todos</option>
                            <option value="ready">Ativo</option>
                            <option value="processing">Processando</option>
                            <option value="error">Erro</option>
                            <option value="disabled">Desativado</option>
                        </select>
                    </label>
                    <label>Tipo
                        <select v-model="knowledgeFilters.type" @change="carregar">
                            <option value="">Todos</option>
                            <option v-for="option in typeOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                        </select>
                    </label>
                    <div class="alinha-direita" v-if="documents.length">
                        <button class="acao-secundaria" type="button" @click="abrirBusca">Testar base de conhecimento</button>
                        <button class="acao-secundaria" type="button" :disabled="!knowledgeEnabled" @click="abrirUpload">Adicionar documento</button>
                    </div>
                </div>
                <div v-if="loading" class="bloco margem">Carregando documentos...</div>
                <div v-else-if="!documents.length" class="bloco margem ia-empty">
                    <h3>{{ emptyCopy.title }}</h3>
                    <p>{{ emptyCopy.body }}</p>
                    <button class="acao-secundaria" type="button" :disabled="!knowledgeEnabled" @click="abrirUpload">
                        {{ emptyCopy.action }}
                    </button>
                </div>
                <div v-else class="bloco margem">
                    <p v-if="feedback" class="ia-feedback">{{ feedback }}</p>
                    <table class="tabela">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Tipo</th>
                                <th>Escopo</th>
                                <th>Módulo</th>
                                <th>Versão</th>
                                <th>Status</th>
                                <th>Upload</th>
                                <th>Indexação</th>
                                <th>Chunks</th>
                                <th>Responsável</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="document in documents"
                                :key="document.id"
                                class="ia-row"
                                @click="abrirDetalhe(document.id)"
                            >
                                <td>{{ document.name }}</td>
                                <td>{{ typeLabel(document.type) }}</td>
                                <td>{{ scopeLabel(document.scope, document.module) }}</td>
                                <td>{{ document.module || '—' }}</td>
                                <td>{{ document.version || '—' }}</td>
                                <td>
                                    <span :class="['ia-status', document.status]">{{ statusLabel(document.status) }}</span>
                                    <span v-if="reindexingIds.includes(document.id)" class="ia-status processing">
                                        Processando documento...
                                    </span>
                                </td>
                                <td>{{ formatDate(document.uploaded_at) }}</td>
                                <td>{{ formatDate(document.indexed_at) }}</td>
                                <td>{{ document.chunk_count ?? '—' }}</td>
                                <td>{{ document.uploaded_by || '—' }}</td>
                                <td>
                                    <button
                                        v-if="statusAction(document.status)"
                                        class="acao-secundaria"
                                        type="button"
                                        :disabled="statusUpdatingIds.includes(document.id)"
                                        @click.stop="alterarStatus(document)"
                                    >
                                        {{ statusUpdatingIds.includes(document.id)
                                            ? 'Atualizando...'
                                            : statusAction(document.status).label }}
                                    </button>
                                    <button class="acao-secundaria" type="button" @click.stop="confirmarExclusao(document)">Excluir</button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            <section v-if="activeTab === 'memory'" class="ia-screen">
                <p class="ia-lead">
                    Fatos duráveis usados pelo assistente. Memórias pessoais são isoladas por usuário;
                    memórias da empresa guardam somente contexto institucional.
                </p>
                <div class="bloco margem ia-toolbar">
                    <label class="ia-search">Buscar
                        <input v-model="memory.filters.q" type="search" placeholder="Conteúdo da memória" @keyup.enter="carregarMemoria">
                    </label>
                    <label>Escopo
                        <select v-model="memory.filters.scope" @change="carregarMemoria">
                            <option value="">Todos</option>
                            <option value="user">Usuário</option>
                            <option value="tenant">Empresa</option>
                        </select>
                    </label>
                    <label>Usuário
                        <select v-model="memory.filters.user_id" @change="carregarMemoria">
                            <option value="">Todos</option>
                            <option v-for="item in access.users" :key="'memory-user-'+item.id" :value="item.id">
                                {{ item.name }}
                            </option>
                        </select>
                    </label>
                    <label>Status
                        <select v-model="memory.filters.status" @change="carregarMemoria">
                            <option value="">Todos</option>
                            <option value="active">Ativa</option>
                            <option value="inactive">Inativa</option>
                        </select>
                    </label>
                    <button class="acao-secundaria" type="button" @click="carregarMemoria">Filtrar</button>
                </div>
                <div v-if="memory.loading" class="bloco margem ia-state">Carregando memórias...</div>
                <div v-else class="bloco margem">
                    <p v-if="!memory.items.length" class="ia-state">Nenhuma memória encontrada.</p>
                    <table v-else class="tabela">
                        <thead>
                            <tr>
                                <th>Escopo</th>
                                <th>Categoria</th>
                                <th>Memória</th>
                                <th>Origem</th>
                                <th>Status</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in memory.items" :key="'memory-'+item.id">
                                <td>{{ item.scope === 'tenant' ? 'Empresa' : (item.user_name || 'Usuário') }}</td>
                                <td>{{ memoryCategory(item.category) }}</td>
                                <td>{{ item.content }}</td>
                                <td>
                                    <small>
                                        {{ item.reinforced_count }} reforço(s)<br>
                                        {{ formatDate(item.last_reinforced_at || item.created_at) }}
                                    </small>
                                </td>
                                <td>
                                    <span class="ia-status" :class="item.status === 'active' ? 'ready' : 'disabled'">
                                        {{ item.status === 'active' ? 'Ativa' : 'Inativa' }}
                                    </span>
                                </td>
                                <td>
                                    <button class="acao-secundaria" type="button" @click="editarMemoria(item)">Editar</button>
                                    <button class="acao-secundaria" type="button" @click="alternarMemoria(item)">
                                        {{ item.status === 'active' ? 'Desativar' : 'Ativar' }}
                                    </button>
                                    <button class="acao-secundaria" type="button" @click="excluirMemoria(item)">Excluir</button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            <section v-if="activeTab === 'instructions'" class="ia-screen">
                <p class="ia-lead">
                    Orientações por módulo usadas pelo assistente. Elas não alteram permissões, ferramentas nem regras de segurança.
                </p>
                <div v-if="instructions.loading" class="bloco margem ia-state">Carregando instruções...</div>
                <form v-else class="bloco margem" @submit.prevent="salvarInstrucoes">
                    <p v-if="instructions.error" class="ia-error">{{ instructions.error }}</p>
                    <p v-if="instructions.feedback" class="ia-feedback">{{ instructions.feedback }}</p>
                    <label v-for="item in instructions.items" :key="item.module">
                        {{ moduleName(item.module) }}
                        <textarea v-model="item.text" rows="4" :maxlength="instructions.maxChars"></textarea>
                        <small>{{ (item.text || '').length }} / {{ instructions.maxChars }}</small>
                    </label>
                    <div class="alinha-direita">
                        <button class="acao-secundaria" type="submit" :disabled="instructions.saving">
                            {{ instructions.saving ? 'Salvando...' : 'Salvar' }}
                        </button>
                    </div>
                </form>
            </section>
        </div>

        <div v-if="showUpload" class="ia-modal" @click.self="fecharUpload">
            <div class="ia-modal-card">
                <h3>Adicionar documento</h3>
                <p v-if="!knowledgeEnabled">A indexação está desabilitada neste ambiente.</p>
                <form v-else @submit.prevent="enviarUpload">
                    <label
                        class="ia-drop"
                        :class="{ ativo: dragging }"
                        @dragover.prevent="dragging = true"
                        @dragleave.prevent="dragging = false"
                        @drop.prevent="soltarArquivo"
                    >
                        <input ref="fileInput" type="file" accept=".pdf,.txt,.md,text/plain,application/pdf" hidden @change="escolherArquivo">
                        <span v-if="form.file">{{ form.file.name }}</span>
                        <span v-else>Arraste um PDF, TXT ou Markdown ou clique para selecionar</span>
                    </label>
                    <label>Nome
                        <input v-model="form.name" type="text" required maxlength="255">
                    </label>
                    <label>Tipo
                        <select v-model="form.type" required>
                            <option v-for="option in typeOptions" :key="option.value" :value="option.value">
                                {{ option.label }}
                            </option>
                        </select>
                    </label>
                    <label>Escopo
                        <select v-model="form.scope" required>
                            <option value="tenant">Toda a organização</option>
                            <option value="module">Módulo específico</option>
                        </select>
                    </label>
                    <label v-if="form.scope === 'module'">Módulo
                        <select v-model="form.module" required>
                            <option v-for="module in modules" :key="module" :value="module">{{ moduleName(module) }}</option>
                        </select>
                    </label>
                    <p v-if="uploadProgress !== null">Enviando arquivo... {{ uploadProgress }}%</p>
                    <p v-if="uploading && uploadProgress === null">Processando documento...</p>
                    <p v-if="formError" class="ia-error">{{ formError }}</p>
                    <div class="alinha-direita">
                        <button class="acao-secundaria" type="button" @click="fecharUpload">Cancelar</button>
                        <button class="acao-secundaria" type="submit" :disabled="uploading">Enviar</button>
                    </div>
                </form>
            </div>
        </div>

        <div v-if="detail" class="ia-modal" @click.self="detail = null">
            <div class="ia-modal-card ia-wide">
                <h3>{{ detail.name }}</h3>
                <p><strong>Arquivo original:</strong> {{ detail.original_filename || '—' }}</p>
                <p><strong>Tipo:</strong> {{ typeLabel(detail.type) }}</p>
                <p><strong>Escopo:</strong> {{ scopeLabel(detail.scope, detail.module) }}</p>
                <p><strong>Módulo:</strong> {{ detail.module || '—' }}</p>
                <p><strong>Versão:</strong> {{ detail.version || '—' }}</p>
                <p><strong>Status:</strong> {{ statusLabel(detail.status) }}</p>
                <p><strong>Hash:</strong> {{ detail.content_hash || '—' }}</p>
                <p><strong>Upload:</strong> {{ formatDate(detail.uploaded_at) }}</p>
                <p><strong>Páginas detectadas:</strong> {{ detail.pages_detected ?? '—' }}</p>
                <p><strong>Modelo de embedding:</strong> {{ detail.embedding_model || '—' }}</p>
                <p><strong>Dimensões:</strong> {{ detail.embedding_dimensions ?? '—' }}</p>
                <p><strong>Indexação:</strong> {{ formatDate(detail.indexed_at) }}</p>
                <p v-if="detail.error_message" class="ia-error">{{ detail.error_message }}</p>
                <div class="alinha-direita m-b">
                    <button v-if="detail.can_download" class="acao-secundaria" type="button" @click="baixarOriginal">
                        Baixar original
                    </button>
                    <button class="acao-secundaria" type="button" :disabled="!knowledgeEnabled" @click="reindexar">
                        Reindexar
                    </button>
                    <button
                        v-if="statusAction(detail.status)"
                        class="acao-secundaria"
                        type="button"
                        :disabled="statusUpdatingIds.includes(detail.id)
                            || (detail.status === 'disabled' && !detail.can_enable)"
                        @click="alterarStatus(detail)"
                    >{{ statusAction(detail.status).label }}</button>
                    <button class="acao-secundaria" type="button" @click="confirmarExclusao(detail)">Excluir</button>
                </div>
                <h4>Texto / chunks</h4>
                <div v-if="!detail.chunks?.length">Nenhum chunk disponível ainda.</div>
                <article v-for="chunk in detail.chunks" :key="chunk.chunk_index" class="ia-chunk">
                    <header>
                        Chunk {{ chunk.chunk_index }}
                        <span v-if="chunk.page_start">• Página {{ chunk.page_start }}</span>
                        <span v-if="chunk.section">• {{ chunk.section }}</span>
                    </header>
                    <p>{{ chunk.content }}</p>
                </article>
            </div>
        </div>

        <div v-if="showSearch" class="ia-modal" @click.self="showSearch = false">
            <div class="ia-modal-card ia-wide">
                <h3>Testar base de conhecimento</h3>
                <p>A consulta usa o mesmo recorte de módulo da tool do assistente.</p>
                <label>Pergunta
                    <textarea v-model="search.query" rows="3" placeholder="O que caracteriza um resultado válido em um projeto da Roboflex?"></textarea>
                </label>
                <label>Filtro
                    <select v-model="search.filter">
                        <option value="module">Módulo (igual ao assistente)</option>
                        <option value="all">Toda a base</option>
                        <option value="document">Documento específico</option>
                    </select>
                </label>
                <label v-if="search.filter === 'module'">Módulo
                    <select v-model="search.module">
                        <option v-for="module in modules" :key="module" :value="module">{{ moduleName(module) }}</option>
                    </select>
                </label>
                <label v-if="search.filter === 'document'">Documento
                    <select v-model="search.documentId">
                        <option v-for="document in documents" :key="document.id" :value="document.id">
                            {{ document.name }}
                        </option>
                    </select>
                </label>
                <div class="alinha-direita">
                    <button class="acao-secundaria" type="button" :disabled="searching" @click="testarBusca">
                        {{ searching ? 'Buscando...' : 'Buscar' }}
                    </button>
                </div>
                <div v-if="searchError" class="ia-error">{{ searchError }}</div>
                <article v-for="(result, index) in searchResults" :key="index" class="ia-result">
                    <h4>{{ result.document_name }}</h4>
                    <p>{{ formatSearchMeta(result) }}</p>
                    <p>Score de fusão {{ formatScore(result.fusion_score ?? result.score) }} • chunk {{ result.chunk_index }}</p>
                    <blockquote>“{{ result.excerpt }}”</blockquote>
                </article>
            </div>
        </div>
    </div>
</template>

<script>
import { useApiError } from '@/composables/useApiError'
import serviceAi from '@/services/serviceAi'
import { isAiKnowledgeUiEnabled, knowledgeMaxFileMb } from '@/utils/ai-governance'
import { THALAMUS_AI_FUNCTIONALITY_ID } from '@/utils/ai-assistant.access'
import {
    ACCESS_MATRIX_MODULES,
    AI_GOVERNANCE_TABS,
    defaultUsageRange,
    formatCost,
    formatDuration,
    formatMeasure,
    formatMeasureMax,
    formatTokens,
    moduleLabel,
} from '@/utils/ai-governance-admin'
import {
    EMPTY_KNOWLEDGE_COPY,
    KNOWLEDGE_EXPLANATION,
    documentsNeedingPoll,
    formatKnowledgeScore,
    formatSearchResultMeta,
    isAllowedKnowledgeFile,
    knowledgeScopeLabel,
    knowledgeStatusAction,
    knowledgeStatusLabel,
    knowledgeTypeLabel,
    nameFromFilename,
} from '@/utils/ai-knowledge-admin'

export default {
    name: 'GestaoDeIaView',
    setup() {
        const { handleError } = useApiError()
        return { handleApiError: handleError }
    },
    data() {
        const range = defaultUsageRange()
        return {
            tabs: AI_GOVERNANCE_TABS,
            activeTab: 'usage',
            loading: false,
            knowledgeEnabled: isAiKnowledgeUiEnabled(),
            documents: [],
            modules: ['projects', 'demands', 'products', 'purchasing', 'production', 'commercial', 'finance', 'people', 'general'],
            accessModules: ACCESS_MATRIX_MODULES,
            maxFileMb: knowledgeMaxFileMb(),
            typeOptions: [
                { value: 'methodology', label: 'Metodologia' },
                { value: 'system_doc', label: 'Documentação de sistema' },
                { value: 'pmo', label: 'PMO' },
                { value: 'policy', label: 'Política' },
                { value: 'other', label: 'Outro' },
            ],
            explanation: KNOWLEDGE_EXPLANATION,
            emptyCopy: EMPTY_KNOWLEDGE_COPY,
            knowledgeFilters: { module: '', status: '', type: '' },
            showUpload: false,
            uploading: false,
            uploadProgress: null,
            formError: '',
            dragging: false,
            form: {
                file: null,
                name: '',
                type: 'methodology',
                scope: 'tenant',
                module: 'projects',
            },
            detail: null,
            showSearch: false,
            searching: false,
            searchError: '',
            searchResults: [],
            search: {
                query: '',
                filter: 'module',
                module: 'projects',
                documentId: null,
            },
            feedback: '',
            reindexingIds: [],
            statusUpdatingIds: [],
            pollHandle: null,
            usage: {
                loading: false,
                currency: 'USD',
                features: [],
                filters: { from: range.from, until: range.until, module: '', feature: '' },
                summary: {},
                series: [],
                byUser: [],
                byModule: [],
                byFeature: [],
                byModel: [],
                latency: { turns: 0, stages: [], recent: [] },
            },
            access: {
                loading: false,
                q: '',
                users: [],
                savingIds: [],
                requestId: 0,
            },
            memory: {
                loading: false,
                items: [],
                filters: { q: '', scope: '', user_id: '', status: '' },
            },
            instructions: {
                loading: false,
                saving: false,
                maxChars: 4000,
                items: [],
                feedback: '',
                error: '',
            },
        }
    },
    computed: {
        accessFunctionalityId() {
            return THALAMUS_AI_FUNCTIONALITY_ID
        },
    },
    async created() {
        await this.carregarConsumo()
        this.iniciarPoll()
    },
    beforeUnmount() {
        this.pararPoll()
    },
    methods: {
        moduleName: moduleLabel,
        formatTokens,
        formatCost,
        formatDuration,
        formatMeasure,
        formatMeasureMax,
        userName(row) {
            if (row && typeof row === 'object' && row.name) return row.name
            const id = row && typeof row === 'object' ? row.key : row
            const found = this.access.users.find((item) => String(item.id) === String(id))
            return found?.name || (id ? `Usuário ${id}` : '—')
        },
        featureName(key) {
            const found = this.usage.features.find((item) => item.value === key)
            return found?.label || key || '—'
        },
        formatDay(value) {
            const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/)
            if (!match) return value || '—'
            return `${match[3]}/${match[2]}/${match[1]}`
        },
        async selecionarAba(id) {
            this.activeTab = id
            if (id === 'usage') await this.carregarConsumo()
            if (id === 'users') await this.carregarAcesso()
            if (id === 'knowledge') await this.carregar()
            if (id === 'memory') {
                if (!this.access.users.length) await this.carregarAcesso({ silent: true })
                await this.carregarMemoria()
            }
            if (id === 'instructions') await this.carregarInstrucoes()
        },
        formInicial() {
            return {
                file: null,
                name: '',
                type: 'methodology',
                scope: 'tenant',
                module: 'projects',
            }
        },
        typeLabel: knowledgeTypeLabel,
        statusLabel: knowledgeStatusLabel,
        statusAction: knowledgeStatusAction,
        scopeLabel: knowledgeScopeLabel,
        formatScore: formatKnowledgeScore,
        formatSearchMeta: formatSearchResultMeta,
        formatDate(value) {
            if (!value) return '—'
            const date = new Date(value)
            if (Number.isNaN(date.getTime())) return '—'
            return date.toLocaleString('pt-BR')
        },
        async carregar() {
            this.loading = true
            try {
                const payload = await serviceAi.listKnowledgeDocuments(this.knowledgeFilters)
                this.documents = payload?.data || []
                const meta = payload?.meta || {}
                this.knowledgeEnabled = meta.knowledge_enabled !== false && isAiKnowledgeUiEnabled()
                if (Array.isArray(meta.modules) && meta.modules.length) {
                    this.modules = meta.modules
                }
                if (meta.max_file_mb) {
                    this.maxFileMb = meta.max_file_mb
                }
                if (Array.isArray(meta.types) && meta.types.length) {
                    this.typeOptions = meta.types
                }
            } catch (error) {
                this.handleApiError(error)
            } finally {
                this.loading = false
            }
        },
        async carregarConsumo() {
            this.usage.loading = true
            try {
                const payload = await serviceAi.getGovernanceUsage({
                    from: this.usage.filters.from,
                    until: this.usage.filters.until,
                    module: this.usage.filters.module || undefined,
                    feature: this.usage.filters.feature || undefined,
                })
                this.usage.summary = payload?.summary || {}
                this.usage.series = payload?.series || []
                this.usage.byUser = payload?.by_user || []
                this.usage.byModule = payload?.by_module || []
                this.usage.byFeature = payload?.by_feature || []
                this.usage.byModel = payload?.by_model || []
                this.usage.latency = payload?.latency || { turns: 0, stages: [], recent: [] }
                this.usage.features = payload?.catalog?.features || []
                this.usage.currency = payload?.catalog?.pricing_currency || 'USD'
                if (payload?.catalog?.modules?.length) {
                    this.modules = payload.catalog.modules
                }
                if (!this.access.users.length) {
                    this.carregarAcesso({ silent: true })
                }
            } catch (error) {
                this.handleApiError(error)
            } finally {
                this.usage.loading = false
            }
        },
        async carregarAcesso({ silent = false } = {}) {
            const requestId = (this.access.requestId || 0) + 1
            this.access.requestId = requestId
            if (!silent) this.access.loading = true
            try {
                const users = []
                let page = 1
                let lastPage = 1
                do {
                    const payload = await serviceAi.listGovernanceUsers({
                        q: this.access.q,
                        page,
                        perPage: 100,
                    })
                    if (requestId !== this.access.requestId) return
                    const batch = Array.isArray(payload?.data) ? payload.data : []
                    users.push(...batch)
                    if (payload?.catalog?.modules?.length) {
                        this.modules = payload.catalog.modules
                    }
                    lastPage = Number(payload?.meta?.last_page || 1)
                    page += 1
                } while (page <= lastPage && page <= 30)
                if (requestId !== this.access.requestId) return
                this.access.users = users
            } catch (error) {
                if (requestId === this.access.requestId) this.handleApiError(error)
            } finally {
                if (!silent && requestId === this.access.requestId) this.access.loading = false
            }
        },
        activeInstructionItems(items) {
            const list = Array.isArray(items) ? items : []
            return this.accessModules.map((module) => ({
                module,
                text: list.find((item) => item.module === module)?.text || '',
            }))
        },
        async carregarInstrucoes() {
            this.instructions.loading = true
            this.instructions.error = ''
            try {
                const payload = await serviceAi.getGovernanceInstructions()
                const data = payload?.data || payload || {}
                this.instructions.maxChars = data.max_chars || 4000
                this.instructions.items = this.activeInstructionItems(data.items)
            } catch (error) {
                this.instructions.error = error?.response?.data?.message || 'Não foi possível carregar as instruções.'
                this.handleApiError(error)
            } finally {
                this.instructions.loading = false
            }
        },
        async salvarInstrucoes() {
            this.instructions.saving = true
            this.instructions.error = ''
            this.instructions.feedback = ''
            try {
                const payload = await serviceAi.saveGovernanceInstructions(this.instructions.items.map((item) => ({
                    module: item.module,
                    text: item.text || '',
                })))
                const data = payload?.data || payload || {}
                this.instructions.items = Array.isArray(data.items)
                    ? this.activeInstructionItems(data.items)
                    : this.instructions.items
                this.instructions.feedback = 'Instruções salvas. O assistente passa a usá-las no próximo turno.'
            } catch (error) {
                this.instructions.error = error?.response?.data?.message || 'Não foi possível salvar as instruções.'
                this.handleApiError(error)
            } finally {
                this.instructions.saving = false
            }
        },
        memoryCategory(category) {
            return {
                preference: 'Preferência',
                role: 'Papel',
                responsibility: 'Responsabilidade',
                working_style: 'Forma de trabalho',
                constraint: 'Restrição',
                process: 'Processo',
                policy: 'Política',
                terminology: 'Terminologia',
                business_context: 'Contexto do negócio',
            }[category] || category
        },
        async carregarMemoria() {
            this.memory.loading = true
            try {
                const filters = Object.fromEntries(
                    Object.entries(this.memory.filters).filter(([, value]) => value !== ''),
                )
                const payload = await serviceAi.getGovernanceMemory({ ...filters, per_page: 100 })
                this.memory.items = payload?.data || []
            } catch (error) {
                this.handleApiError(error)
            } finally {
                this.memory.loading = false
            }
        },
        async editarMemoria(item) {
            const content = window.prompt('Edite a memória:', item.content)
            if (content === null || content.trim() === item.content) return
            try {
                await serviceAi.updateGovernanceMemory(item.id, { content: content.trim() })
                await this.carregarMemoria()
            } catch (error) {
                this.handleApiError(error)
            }
        },
        async alternarMemoria(item) {
            try {
                await serviceAi.updateGovernanceMemory(item.id, {
                    status: item.status === 'active' ? 'inactive' : 'active',
                })
                await this.carregarMemoria()
            } catch (error) {
                this.handleApiError(error)
            }
        },
        async excluirMemoria(item) {
            if (!window.confirm('Excluir esta memória permanentemente?')) return
            try {
                await serviceAi.deleteGovernanceMemory(item.id)
                await this.carregarMemoria()
            } catch (error) {
                this.handleApiError(error)
            }
        },
        async persistPolicy(item, patch) {
            this.access.savingIds.push(item.id)
            try {
                const payload = await serviceAi.saveGovernanceUserPolicy(item.id, {
                    enabled: item.enabled,
                    module_overrides: item.module_overrides,
                    feature_overrides: item.feature_overrides,
                    ...patch,
                })
                const data = payload?.data || payload
                this.access.users = this.access.users.map((current) => (
                    current.id === item.id ? { ...current, ...data } : current
                ))
            } catch (error) {
                this.handleApiError(error)
                await this.carregarAcesso()
            } finally {
                this.access.savingIds = this.access.savingIds.filter((id) => id !== item.id)
            }
        },
        toggleEnabled(item, event) {
            this.persistPolicy(item, { enabled: event.target.checked })
        },
        toggleModule(item, module, event) {
            this.persistPolicy(item, {
                module_overrides: {
                    ...(item.module_overrides || {}),
                    [module]: event.target.checked,
                },
            })
        },
        toggleFeature(item, feature, event) {
            this.persistPolicy(item, {
                feature_overrides: {
                    ...(item.feature_overrides || {}),
                    [feature]: event.target.checked,
                },
            })
        },
        iniciarPoll() {
            this.pararPoll()
            this.pollHandle = window.setInterval(async () => {
                if (this.activeTab !== 'knowledge') return
                if (!documentsNeedingPoll(this.documents) && !this.reindexingIds.length) {
                    return
                }
                try {
                    const payload = await serviceAi.listKnowledgeDocuments(this.knowledgeFilters)
                    this.documents = payload?.data || []
                    this.reindexingIds = this.reindexingIds.filter((id) => {
                        const current = this.documents.find((document) => document.id === id)
                        return current && (current.status === 'processing' || current.status === 'uploading')
                    })
                    if (this.detail) {
                        const current = this.documents.find((document) => document.id === this.detail.id)
                        if (current && current.status !== this.detail.status) {
                            await this.abrirDetalhe(this.detail.id)
                        }
                    }
                } catch {
                    // polling silencioso
                }
            }, 4000)
        },
        pararPoll() {
            if (this.pollHandle) {
                window.clearInterval(this.pollHandle)
                this.pollHandle = null
            }
        },
        abrirUpload() {
            this.form = this.formInicial()
            this.formError = ''
            this.uploadProgress = null
            this.showUpload = true
        },
        fecharUpload() {
            if (!this.uploading) {
                this.showUpload = false
            }
        },
        escolherArquivo(event) {
            this.definirArquivo(event.target.files?.[0])
        },
        soltarArquivo(event) {
            this.dragging = false
            this.definirArquivo(event.dataTransfer?.files?.[0])
        },
        definirArquivo(file) {
            const validation = isAllowedKnowledgeFile(file, this.maxFileMb)
            if (!validation.ok) {
                this.formError = validation.message
                return
            }
            this.form.file = file
            if (!this.form.name) {
                this.form.name = nameFromFilename(file.name)
            }
            this.formError = ''
        },
        async enviarUpload() {
            const validation = isAllowedKnowledgeFile(this.form.file, this.maxFileMb)
            if (!validation.ok) {
                this.formError = validation.message
                return
            }
            this.uploading = true
            this.uploadProgress = 0
            this.feedback = 'Processando documento...'
            try {
                const formData = new FormData()
                formData.append('file', this.form.file)
                formData.append('name', this.form.name)
                formData.append('type', this.form.type)
                formData.append('scope', this.form.scope)
                if (this.form.scope === 'module') {
                    formData.append('module', this.form.module)
                }
                await serviceAi.uploadKnowledgeDocument(formData, {
                    onUploadProgress: (event) => {
                        if (!event.total) {
                            this.uploadProgress = null
                            return
                        }
                        this.uploadProgress = Math.round((event.loaded / event.total) * 100)
                    },
                })
                this.showUpload = false
                this.feedback = 'Documento enviado. A indexação continua em segundo plano.'
                await this.carregar()
            } catch (error) {
                this.formError = error?.response?.data?.message || 'Não foi possível enviar o documento.'
                this.handleApiError(error)
            } finally {
                this.uploading = false
                this.uploadProgress = null
            }
        },
        async abrirDetalhe(id) {
            try {
                const payload = await serviceAi.getKnowledgeDocument(id)
                this.detail = payload?.data || null
            } catch (error) {
                this.handleApiError(error)
            }
        },
        async baixarOriginal() {
            try {
                const response = await serviceAi.downloadKnowledgeFile(this.detail.id)
                const blob = new Blob([response.data], { type: response.headers?.['content-type'] })
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = this.detail.original_filename || 'documento'
                link.click()
                window.URL.revokeObjectURL(url)
            } catch (error) {
                this.handleApiError(error)
            }
        },
        async reindexar() {
            if (!window.confirm('Reindexar este documento? A última versão válida permanece disponível durante o processamento.')) {
                return
            }
            try {
                this.reindexingIds.push(this.detail.id)
                this.feedback = 'Processando documento...'
                await serviceAi.reindexKnowledgeDocument(this.detail.id)
                await this.carregar()
            } catch (error) {
                this.reindexingIds = this.reindexingIds.filter((id) => id !== this.detail.id)
                this.handleApiError(error)
            }
        },
        async alterarStatus(document) {
            const action = knowledgeStatusAction(document?.status)
            if (!action) {
                return
            }
            if (
                action.targetStatus === 'disabled'
                && !window.confirm(
                    'O documento deixará de ser utilizado nas respostas da IA, mas não será excluído. Deseja desativá-lo?',
                )
            ) {
                return
            }
            this.statusUpdatingIds.push(document.id)
            try {
                await serviceAi.updateKnowledgeDocumentStatus(document.id, action.targetStatus)
                this.feedback = action.targetStatus === 'disabled'
                    ? 'Documento desativado. O histórico e o arquivo foram preservados.'
                    : 'Documento reativado e disponível para retrieval.'
                await this.carregar()
                if (this.detail?.id === document.id) {
                    await this.abrirDetalhe(document.id)
                }
            } catch (error) {
                this.handleApiError(error)
            } finally {
                this.statusUpdatingIds = this.statusUpdatingIds.filter((id) => id !== document.id)
            }
        },
        async confirmarExclusao(document) {
            if (!window.confirm(`Excluir "${document.name}"? Arquivo e trechos sairão da IA de forma permanente.`)) {
                return
            }
            try {
                await serviceAi.deleteKnowledgeDocument(document.id)
                this.feedback = 'Documento excluído. O conhecimento correspondente foi removido da IA.'
                if (this.detail?.id === document.id) {
                    this.detail = null
                }
                await this.carregar()
            } catch (error) {
                this.handleApiError(error)
            }
        },
        abrirBusca() {
            this.searchResults = []
            this.searchError = ''
            this.showSearch = true
            if (this.documents.length && !this.search.documentId) {
                this.search.documentId = this.documents[0].id
            }
        },
        async testarBusca() {
            this.searching = true
            this.searchError = ''
            try {
                const payload = await serviceAi.searchKnowledge({
                    query: this.search.query,
                    module: this.search.filter === 'module' ? this.search.module : null,
                    documentId: this.search.filter === 'document' ? this.search.documentId : null,
                })
                this.searchResults = payload?.data || []
                if (!this.searchResults.length) {
                    this.searchError = 'Nenhum trecho encontrado para esta consulta.'
                }
            } catch (error) {
                this.searchError = error?.response?.data?.message || 'Não foi possível testar a base.'
                this.handleApiError(error)
            } finally {
                this.searching = false
            }
        },
    },
}
</script>

<style scoped>
.ia-copy p,
.ia-empty p {
    max-width: 52rem;
}
.ia-empty h3 {
    margin-bottom: 0.5rem;
}
.ia-row {
    cursor: pointer;
}
.ia-status {
    border-radius: 999px;
    padding: 0.15rem 0.55rem;
    font-size: 12px;
}
.ia-status.processing,
.ia-status.uploading {
    background: #fef3c7;
    color: #92400e;
}
.ia-status.ready {
    background: #dcfce7;
    color: #166534;
}
.ia-status.error {
    background: #fee2e2;
    color: #991b1b;
}
.ia-status.disabled {
    background: #e5e7eb;
    color: #374151;
}
.ia-feedback,
.ia-error {
    margin-bottom: 0.75rem;
}
.ia-error {
    color: #991b1b;
}
.ia-badge {
    margin-left: 0.35rem;
    font-size: 11px;
    opacity: 0.75;
}
.ia-screen {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}
.ia-screen form label {
    display: block;
    margin-bottom: 0.85rem;
}
.ia-screen form textarea,
.ia-screen form input[type="number"] {
    display: block;
    width: 100%;
    max-width: 52rem;
    margin-top: 0.35rem;
}
.ia-screen > .bloco.margem {
    margin-top: 0;
}
.ia-lead {
    margin: 0;
    max-width: 52rem;
    color: #475569;
}
.ia-state {
    margin: 0;
    padding: 1.25rem 1.4rem;
}
.ia-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 0.85rem 1rem;
    align-items: flex-end;
    padding: 1rem 1.15rem;
}
.ia-toolbar label {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    min-width: 10rem;
    font-size: 0.85rem;
}
.ia-toolbar input,
.ia-toolbar select {
    min-width: 11rem;
}
.ia-search {
    flex: 1 1 18rem;
}
.ia-count {
    margin-left: auto;
    padding-bottom: 0.45rem;
    color: #64748b;
}
.ia-metrics {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
}
.ia-metrics-4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
}
.ia-latency-lead {
    padding: 0 1.15rem 0.75rem;
}
.ia-metrics small {
    display: block;
    margin-top: 0.35rem;
    color: #64748b;
}
.ia-metrics > div {
    padding: 1.35rem 1.5rem;
}
.ia-metrics > div + div {
    box-shadow: -1px 0 #e5e7eb;
}
.ia-metrics span {
    display: block;
    font-size: 0.78rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #64748b;
}
.ia-metrics strong {
    display: block;
    margin-top: 0.35rem;
    font-size: 1.85rem;
    font-weight: 650;
    line-height: 1.1;
}
.ia-columns {
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
    gap: 1rem;
    align-items: start;
}
.ia-columns-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}
.ia-panel-head {
    padding: 1rem 1.15rem 0.15rem;
}
.ia-panel-head h3 {
    margin: 0;
    font-size: 1rem;
}
.ia-access-card {
    overflow: auto;
}
table.tabela.ia-access th.alinha-centro,
table.tabela.ia-access td.alinha-centro {
    text-align: center;
}
.ia-columns th.alinha-direita,
.ia-columns td.alinha-direita {
    text-align: right;
    white-space: nowrap;
}
.ia-access {
    min-width: 52rem;
}
.ia-person {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 16rem;
}
.ia-person small,
.ia-saving {
    color: #64748b;
}
.ia-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: end;
}
@media (max-width: 1100px) {
    .ia-metrics,
    .ia-columns,
    .ia-columns-3 {
        grid-template-columns: minmax(0, 1fr);
    }
    .ia-metrics > div + div {
        box-shadow: inset 0 1px #e5e7eb;
    }
}
.ia-modal {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.45);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    overflow: auto;
    padding: 2rem 1rem;
    z-index: 40;
}
.ia-modal-card {
    background: #fff;
    border-radius: 8px;
    padding: 1.25rem;
    width: min(36rem, 100%);
}
.ia-modal-card.ia-wide {
    width: min(52rem, 100%);
}
.ia-modal-card label,
.ia-modal-card input,
.ia-modal-card select,
.ia-modal-card textarea {
    display: block;
    width: 100%;
    margin-bottom: 0.75rem;
}
.ia-drop {
    display: block;
    border: 1px dashed #94a3b8;
    border-radius: 8px;
    padding: 1.25rem;
    text-align: center;
    margin-bottom: 1rem;
    cursor: pointer;
}
.ia-drop.ativo {
    border-color: #2563eb;
    background: #eff6ff;
}
.ia-chunk,
.ia-result {
    border-top: 1px solid #e5e7eb;
    padding-top: 0.75rem;
    margin-top: 0.75rem;
}
.ia-result blockquote {
    margin: 0.5rem 0 0;
}
</style>
